import { Location, isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  UrlTree,
} from '@angular/router';

import { AuthService } from '@personal-area/global/authorization/services';
import { PwaDetectorService } from '@personal-area/global/browser-detection';
import { AutoRegistrationService } from '@personal-area/global/social-authorization/services';

interface HandleSocialCallbackParams {
  autoRegService: AutoRegistrationService;
  router: Router;
  location: Location;
  socialCallback: string;
}

/**
 * Обрабатывает социальный callback и возвращает новый UrlTree
 */
function handleSocialCallbackAndRedirect(
  params: HandleSocialCallbackParams,
): UrlTree {
  const { autoRegService, router, location, socialCallback } = params;

  autoRegService.resetSocialAuthCallback();
  const newCallback = socialCallback.replace('/cabinet', '');
  location.replaceState(newCallback);

  const [path, queryString] = newCallback.split('?');
  const queryParams = queryString
    ? Object.fromEntries(new URLSearchParams(queryString).entries())
    : {};

  return router.createUrlTree([path], { queryParams });
}

export const guestGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
): boolean | UrlTree => {
  const platformId = inject(PLATFORM_ID);
  const router = inject(Router);
  const location = inject(Location);
  const authService = inject(AuthService);
  const autoRegService = inject(AutoRegistrationService);
  const pwaDetector = inject(PwaDetectorService);

  const isSocial = autoRegService.checkSocial();
  const isBrowser = isPlatformBrowser(platformId);
  const socialCallback = autoRegService.socialAuthCallback;

  if (!authService.isLoggedIn) {
    if (isBrowser && isSocial && socialCallback) {
      if (
        socialCallback.startsWith('/cabinet') &&
        socialCallback !== window.location.pathname + window.location.search
      ) {
        return handleSocialCallbackAndRedirect({
          autoRegService,
          router,
          location,
          socialCallback,
        });
      } else {
        autoRegService.resetSocialAuthCallback();
      }
    }
    return true;
  } else if (isBrowser && socialCallback && isSocial) {
    return handleSocialCallbackAndRedirect({
      autoRegService,
      router,
      location,
      socialCallback,
    });
  } else {
    const target = pwaDetector.isInPartnerPwaMode()
      ? '/partner/dashboard'
      : '/dashboard';
    return router.createUrlTree([target], { queryParams: route.queryParams });
  }
};
